import React, { useMemo, useState } from "react";
import styles from "./GenericStepper.module.css";

const GenericStepper = ({
    steps = [],
    finishMessage = "Processo concluído com sucesso!",
    allowRestart = false,
    restartButtonLabel = "Recomeçar",
    startTitle = "",
    startDescription = "",
    startButtonLabel = "Começar",
    emitFinish = () => {}
}) => {
    const [started, setStarted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [finished, setFinished] = useState(false);
    const [loadingNext, setLoadingNext] = useState(false);

    const stepRef = React.useRef(null);

    const totalSteps = steps.length;
    const currentStepData = useMemo(() => steps[currentStep], [steps, currentStep]);

    const isStepCompleted = (index) => completedSteps.includes(index);

    const isStepLocked = (index) => {
        if (finished) return true;

        const canAccess =
            index <= currentStep || isStepCompleted(index - 1);

        return !canAccess;
    };

    const handleNext = async () => {
        if (loadingNext) return;

        try {
            setLoadingNext(true);

            if (stepRef.current?.onNext) {
                const canProceed = await stepRef.current.onNext();

                if (!canProceed) return;
            }

            if (!isStepCompleted(currentStep)) {
                setCompletedSteps((prev) => [...prev, currentStep]);
            }

            if (currentStep < totalSteps - 1) {
                setCurrentStep((prev) => prev + 1);
            } else {
                emitFinish()
                setFinished(true);
            }
        } finally {
            setLoadingNext(false);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const handleStepClick = (index) => {
        if (isStepLocked(index)) return;
        setCurrentStep(index);
    };

    const markAsCompleted = () => {
        if (!isStepCompleted(currentStep)) {
            setCompletedSteps((prev) => [...prev, currentStep]);
        }
    };

    const restartProcess = () => {
        setStarted(false);
        setCurrentStep(0);
        setCompletedSteps([]);
        setFinished(false);
    };

    if (!steps.length) {
        return <div className={styles.empty}>Nenhuma etapa disponível.</div>;
    }

    const StepComponent = currentStepData?.component;

    return (
        <div className={styles.container}>
            {!started ? (
                <div className={styles.startContent}>
                    <h2 className={styles.startTitle}>{startTitle}</h2>

                    {startDescription && (
                        <p className={styles.startDescription}>
                            {startDescription}
                        </p>
                    )}

                    <button
                        className={styles.buttonPrimary}
                        onClick={() => setStarted(true)}
                    >
                        {startButtonLabel}
                    </button>
                </div>
            ) : (
                <>
                    <div className={styles.stepperHeader}>
                        {steps.map((step, index) => (
                            <React.Fragment key={index}>
                                <div
                                    className={`
                                        ${styles.stepItem}
                                        ${index === currentStep && !finished ? styles.active : ""}
                                        ${isStepCompleted(index) || finished ? styles.completed : ""}
                                        ${isStepLocked(index) ? styles.disabledStep : ""}
                                    `}
                                    onClick={() => handleStepClick(index)}
                                >
                                    <div
                                        className={`
                                            ${styles.circle}
                                            ${isStepCompleted(index) || finished ? styles.completedCircle : ""}
                                        `}
                                    >
                                        {index + 1}
                                    </div>
                                    <span className={styles.title}>{step.titulo}</span>
                                </div>

                                {index < steps.length - 1 && (
                                    <div
                                        className={`
                                            ${styles.line}
                                            ${(isStepCompleted(index) || finished) && (isStepCompleted(index + 1) || finished)
                                                ? styles.fullLine
                                                : (isStepCompleted(index) || finished) ? styles.halfLine : ""
                                            }
                                            ${finished ? styles.lineFinished : ""}
                                        `}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className={styles.content}>
                        {finished ? (
                            <div className={styles.finishedContent}>
                                <h2 className={styles.finishedTitle}>Finalizado</h2>
                                <p className={styles.finishedMessage}>
                                    {finishMessage}
                                </p>

                                {allowRestart && (
                                    <button
                                        className={styles.buttonPrimary}
                                        onClick={restartProcess}
                                    >
                                        {restartButtonLabel}
                                    </button>
                                )}
                            </div>
                        ) : (
                            StepComponent && (
                                <StepComponent
                                    ref={stepRef}
                                    currentStep={currentStep}
                                    totalSteps={totalSteps}
                                    markAsCompleted={markAsCompleted}
                                />
                            )
                        )}
                    </div>
                    {!finished && (
                        <>
                            <hr />
                            <div className={styles.actions}>
                                <button
                                    className={styles.buttonSecondary}
                                    onClick={handleBack}
                                    disabled={currentStep === 0}
                                >
                                    Voltar
                                </button>

                                <button
                                    className={styles.buttonPrimary}
                                    onClick={handleNext}
                                    disabled={loadingNext}
                                >
                                    {loadingNext
                                        ? "Processando..."
                                        : currentStep === totalSteps - 1
                                            ? "Finalizar"
                                            : "Próximo"}
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default GenericStepper;